import { columnType, getCellValue } from "./columnValues";
import { isActiveFilterItem } from "./filterOperators";
import type {
  ColumnType,
  DataTableColDef,
  FilterItem,
  FilterModel,
  SortModel,
} from "./types";

const collator = new Intl.Collator();

function isNil(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function isBlank(value: unknown): boolean {
  return isNil(value) || value === "";
}

function toTime(value: unknown): number {
  return value instanceof Date
    ? value.getTime()
    : new Date(String(value)).getTime();
}

// A date filter compares whole days, so "2026-10-14" matches any time that day.
function toDay(value: unknown): string {
  if (value instanceof Date) {
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${value.getFullYear()}-${month}-${day}`;
  }
  return String(value).slice(0, 10);
}

function compareNumbers(operator: string, cell: number, filter: number) {
  switch (operator) {
    case "=":
      return cell === filter;
    case "!=":
      return cell !== filter;
    case ">":
      return cell > filter;
    case ">=":
      return cell >= filter;
    case "<":
      return cell < filter;
    case "<=":
      return cell <= filter;
    default:
      return true;
  }
}

function compareDays(operator: string, cell: string, filter: string) {
  switch (operator) {
    case "is":
      return cell === filter;
    case "not":
      return cell !== filter;
    case "after":
      return cell > filter;
    case "onOrAfter":
      return cell >= filter;
    case "before":
      return cell < filter;
    case "onOrBefore":
      return cell <= filter;
    default:
      return true;
  }
}

function compareText(operator: string, cell: string, filter: string) {
  const left = cell.toLowerCase();
  const right = filter.toLowerCase();
  switch (operator) {
    case "contains":
      return left.includes(right);
    case "equals":
      return left === right;
    case "startsWith":
      return left.startsWith(right);
    case "endsWith":
      return left.endsWith(right);
    default:
      return true;
  }
}

function matchesItem(item: FilterItem, type: ColumnType, cell: unknown) {
  if (item.operator === "isEmpty") return isBlank(cell);
  if (item.operator === "isNotEmpty") return !isBlank(cell);
  if (item.operator === "isAnyOf") {
    const options = (item.value as unknown[]).map(String);
    return !isNil(cell) && options.includes(String(cell));
  }
  // An empty cell never matches a value filter, except "is not" / "!=".
  if (isNil(cell)) return item.operator === "not" || item.operator === "!=";
  switch (type) {
    case "number":
      return compareNumbers(item.operator, Number(cell), Number(item.value));
    case "date":
      return compareDays(item.operator, toDay(cell), toDay(item.value));
    case "boolean":
      return String(Boolean(cell)) === String(item.value);
    case "singleSelect":
      return item.operator === "not"
        ? String(cell) !== String(item.value)
        : String(cell) === String(item.value);
    default:
      return compareText(item.operator, String(cell), String(item.value));
  }
}

export function filterRows<R>(
  rows: R[],
  columns: DataTableColDef<R>[],
  model: FilterModel,
): R[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const active = model.items.filter((item) => {
    const column = byField.get(item.field);
    return column && isActiveFilterItem(item, column);
  });
  if (active.length === 0) return rows;

  const matchesAny = model.logicOperator === "or";
  return rows.filter((row) => {
    const results = active.map((item) => {
      const column = byField.get(item.field) as DataTableColDef<R>;
      return matchesItem(item, columnType(column), getCellValue(column, row));
    });
    return matchesAny ? results.some(Boolean) : results.every(Boolean);
  });
}

// Empty cells sort first ascending, like the MUI grid this replaces.
function compareValues(type: ColumnType, left: unknown, right: unknown) {
  if (isNil(left) && isNil(right)) return 0;
  if (isNil(left)) return -1;
  if (isNil(right)) return 1;
  if (type === "number" || type === "boolean") {
    return Number(left) - Number(right);
  }
  if (type === "date") return toTime(left) - toTime(right);
  return collator.compare(String(left), String(right));
}

// Sort keys that match no column are skipped, so rows keep their given order.
export function sortRows<R>(
  rows: R[],
  columns: DataTableColDef<R>[],
  model: SortModel,
): R[] {
  const byField = new Map(columns.map((column) => [column.field, column]));
  const keys = model.flatMap(({ field, sort }) => {
    const column = byField.get(field);
    return column ? [{ column, direction: sort === "desc" ? -1 : 1 }] : [];
  });
  if (keys.length === 0) return rows;

  return [...rows].sort((a, b) => {
    for (const { column, direction } of keys) {
      const result = compareValues(
        columnType(column),
        getCellValue(column, a),
        getCellValue(column, b),
      );
      if (result !== 0) return result * direction;
    }
    return 0;
  });
}
